/**
 * Réglages de validation, PAR TYPE DE DÉCISION (cadrage Dove du 2026-08-05).
 *
 * Dove valide tout — mais exige de pouvoir valider PAR LOT, et d'activer un
 * mode automatique **décision par décision**. Ce n'est donc pas un interrupteur
 * global : relancer un retard de production et envoyer un message marketing
 * n'engagent ni le même risque ni la même confiance.
 *
 * Le passage en « automatique » est une DÉLÉGATION DE POUVOIR : il s'inscrit au
 * journal d'audit, avec son auteur et son motif. Une automatisation qu'on ne
 * peut pas relire est une automatisation qu'on ne peut pas défaire.
 */
import { z } from "zod";

export const MODES_VALIDATION = ["proposer", "lot", "automatique"] as const;
export type ModeValidation = (typeof MODES_VALIDATION)[number];

export const MODE_LABELS: Record<ModeValidation, string> = {
  proposer: "Proposer — tu valides une par une",
  lot: "Valider par lot — tu coches, tu confirmes en une fois",
  automatique: "Automatique — part sans validation",
};

/**
 * Les types de décision, tels que Dove les a énoncés.
 *
 * `risque` classe ce que coûte une erreur : c'est ce qui justifie qu'un type
 * puisse passer en automatique et pas un autre. Une note interne mal rédigée se
 * corrige ; un message marketing parti chez 1 253 cabinets, non.
 */
export const TYPES_DECISION: ReadonlyArray<{
  id: string;
  libelle: string;
  description: string;
  risque: "interne" | "sortant";
  defaut: ModeValidation;
}> = [
  {
    id: "note_automatique",
    libelle: "Note automatique sur une fiche",
    description: "Résumé d'un échange ou d'un événement, écrit dans la chronologie du cabinet.",
    risque: "interne",
    defaut: "proposer",
  },
  {
    id: "relance_retard",
    libelle: "Relance d'un travail en retard",
    description: "Message au cabinet quand une échéance est menacée ou dépassée (§7.B).",
    risque: "sortant",
    defaut: "proposer",
  },
  {
    id: "relance_financiere",
    libelle: "Relance financière",
    description: "Rappel d'une facture échue (§7.C).",
    risque: "sortant",
    defaut: "proposer",
  },
  {
    id: "relance_relation",
    libelle: "Relance de reprise de contact",
    description: "Message à un praticien silencieux depuis plus longtemps que sa cadence (§7.A).",
    risque: "sortant",
    defaut: "proposer",
  },
  {
    id: "notification_partage",
    libelle: "Notification de dépôt au praticien",
    description:
      "E-mail au praticien quand le laboratoire dépose un fichier ou un message dans un dossier partagé (lot G, décision n° 12).",
    risque: "sortant",
    defaut: "proposer",
  },
  {
    id: "alerte_interne",
    libelle: "Alerte à l'équipe",
    description: "Notification interne (mail, WhatsApp, SMS) sur un événement à traiter.",
    risque: "interne",
    defaut: "proposer",
  },
  {
    id: "promotion",
    libelle: "Promotion commerciale",
    description: "Offre adressée à un cabinet ou à un groupe de cabinets.",
    risque: "sortant",
    defaut: "proposer",
  },
  {
    id: "message_marketing",
    libelle: "Message marketing",
    description: "Communication de masse — la décision la plus coûteuse à rattraper.",
    risque: "sortant",
    defaut: "proposer",
  },
  {
    id: "attente_dentiste",
    libelle: "Attente du dentiste : mail au praticien",
    description:
      "Mail au praticien lors des trois réveils d'un travail en attente d'éléments manquants.",
    risque: "sortant",
    defaut: "proposer",
  },
];

export const ReglageValidationSchema = z.object({
  id: z.string(),
  libelle: z.string(),
  description: z.string(),
  risque: z.enum(["interne", "sortant"]),
  mode: z.enum(MODES_VALIDATION),
  modifiePar: z.string().nullable(),
  modifieAt: z.string().nullable(),
  motif: z.string().nullable(),
});
export type ReglageValidation = z.infer<typeof ReglageValidationSchema>;

export const ReglagesValidationResponseSchema = z.object({
  reglages: z.array(ReglageValidationSchema),
});
export type ReglagesValidationResponse = z.infer<typeof ReglagesValidationResponseSchema>;

/**
 * Un motif est EXIGÉ pour passer en automatique.
 *
 * Pas pour la forme : dans six mois, « pourquoi les relances financières
 * partent-elles seules ? » doit avoir une réponse écrite. Revenir vers un mode
 * plus prudent, en revanche, ne demande rien — on ne met pas de friction sur le
 * geste qui réduit le risque.
 */
export function motifRequis(vers: ModeValidation): boolean {
  return vers === "automatique";
}
